import { FC, useEffect, useRef, useState } from 'react';
import { useOnFarmActionBlocked } from '../../../hooks/useOnFarmActionBlocked';
import UploadAttachmentsModal from '../../../components/upload-image-modal/UploadAttachmentsModal';
import UploadImage from '../../../components/svgs/UploadImage.svg';
import OnFarmAnimalInfoForm from './OnFarmAnimalInfoForm';
import { uploadOnFarmAnimalAttachment } from '../../../api/onFarmAnimalsApi';
import { createOnFarmAnimal } from '../../../services/onFarmInventory.service';
import { showToast } from '../../../services/toast.service';
import { PromiseError, toastMessages } from '../../../constants/errorMessages';
import { ANIMAL_CONSTANTS, BUTTON_CONSTANTS } from '../../../constants/common';
import { Genders } from '../../../types/enums/genders.enum';
import { Animal } from '../../../types/interfaces/animal.interfaces';
import { Attachment } from '../../../types/interfaces/attachment.interfaces';

type OnFarmAddAnimalProps = {
  onCancel: (animalId?: number) => void;
};

const OnFarmAddAnimal: FC<OnFarmAddAnimalProps> = ({ onCancel }): JSX.Element => {
  const isOnFarmLocked = useOnFarmActionBlocked();
  const [animalFormIsValid, setAnimalFormIsValid] = useState(false);
  const [validate, setValidate] = useState(false);
  const [creatingAnimal, setCreatingAnimal] = useState(false);
  const [isUploadImageModalOpen, setIsUploadImageModalOpen] = useState(false);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<number | null>(null);
  const pendingFilesRef = useRef<Map<number, File>>(new Map());
  const [animalInfo, setAnimalInfo] = useState<Animal>({
    name: '',
    gender: Genders.Default,
    birthDate: undefined,
    sire: '',
    dam: '',
    dna: '',
    code: '',
    registration: '',
    description: '',
    breed: { name: '', species: { name: '' } },
    attachments: [],
  });

  const attachments = animalInfo.attachments ?? [];
  const selectedAttachment = attachments.find(attachment => attachment.attachmentId === selectedAttachmentId) ?? attachments[0];

  useEffect(() => {
    if (!attachments.length) {
      setSelectedAttachmentId(null);
      return;
    }
    if (!attachments.some(attachment => attachment.attachmentId === selectedAttachmentId)) {
      setSelectedAttachmentId(attachments[0].attachmentId);
    }
  }, [attachments, selectedAttachmentId]);

  const handlePostPendingImages = async (image: FormData) => {
    const files = image.getAll('attachments').filter((entry): entry is File => entry instanceof File);
    const nextIdBase = Date.now();
    const newAttachments: Attachment[] = files.map((file, index) => {
      const attachmentId = nextIdBase + index;
      pendingFilesRef.current.set(attachmentId, file);
      return {
        attachmentId,
        name: file.name,
        accessToken: URL.createObjectURL(file),
      };
    });

    setAnimalInfo(prev => ({
      ...prev,
      attachments: [...(prev.attachments ?? []), ...newAttachments],
    }));
    setIsUploadImageModalOpen(false);
  };

  const handleDeletePendingAttachments = (attachmentIds: number[]) => {
    attachmentIds.forEach(id => {
      const attachment = attachments.find(item => item.attachmentId === id);
      if (attachment?.accessToken.startsWith('blob:')) {
        URL.revokeObjectURL(attachment.accessToken);
      }
      pendingFilesRef.current.delete(id);
    });
    setAnimalInfo(prev => ({
      ...prev,
      attachments: (prev.attachments ?? []).filter(attachment => !attachmentIds.includes(attachment.attachmentId)),
    }));
  };

  const createNewAnimal = async () => {
    if (creatingAnimal || isOnFarmLocked) return;

    setValidate(true);
    if (!animalFormIsValid) return;

    setCreatingAnimal(true);
    try {
      const { data: newAnimalId } = await createOnFarmAnimal({
        name: animalInfo.name.trim(),
        code: animalInfo.code.trim(),
        dna: animalInfo.dna?.trim(),
        registration: animalInfo.registration?.trim(),
        gender: animalInfo.gender,
        birthDate: animalInfo.birthDate,
        deathDate: animalInfo.deathDate,
        sire: animalInfo.sire?.trim(),
        dam: animalInfo.dam?.trim(),
        description: animalInfo.description?.trim(),
        earTag: animalInfo.earTag?.trim(),
        scrapieTag: animalInfo.scrapieTag?.trim(),
        rfidEid: animalInfo.rfidEid?.trim(),
        breedId: animalInfo.breed?.breedId ?? 0,
      });

      const pendingFiles = Array.from(pendingFilesRef.current.values());
      if (pendingFiles.length > 0) {
        const formData = new FormData();
        pendingFiles.forEach(file => formData.append('attachments', file));
        await uploadOnFarmAnimalAttachment(newAnimalId, formData);
      }

      showToast.success(toastMessages.ANIMAL_CREATED);
      onCancel(newAnimalId);
    } catch (error: any) {
      if (error.code === PromiseError.NETWORK) {
        showToast.error(toastMessages.NETWORK);
      } else {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    } finally {
      setCreatingAnimal(false);
    }
  };

  return (
    <div className="on-farm-add-animal">
      <UploadAttachmentsModal
        isOpen={isUploadImageModalOpen}
        onClose={() => setIsUploadImageModalOpen(false)}
        postAttachments={handlePostPendingImages}
        existingAttachments={attachments}
        animalId="pending"
        getAttachmentURL={(_animalId, _attachmentId, accessToken) => accessToken}
        handleDeleteAttachments={handleDeletePendingAttachments}
        handleSubmitNavigation={() => setIsUploadImageModalOpen(false)}
      />
      <div className="on-farm-add-animal-header">
        <h4>{ANIMAL_CONSTANTS.ADD_NEW_ANIMAL}</h4>
      </div>
      <div className="row">
        <h2>{ANIMAL_CONSTANTS.ANIMAL_INFO}</h2>
      </div>
      <div className="on-farm-animal-info-container">
        <div className="on-farm-animal-info-image">
          <button
            type="button"
            className="upload-image-button"
            onClick={() => setIsUploadImageModalOpen(true)}
            disabled={isOnFarmLocked}
            aria-label={BUTTON_CONSTANTS.CHOOSE_FILES}>
            <UploadImage className="upload-image-icon" />
          </button>
          {selectedAttachment ? (
            <img src={selectedAttachment.accessToken} alt="Animal Photo" />
          ) : (
            <div className="no-animal-image">{ANIMAL_CONSTANTS.NO_IMAGE}</div>
          )}
        </div>
        <div className="on-farm-animal-info-column">
          <OnFarmAnimalInfoForm
            animalInfo={animalInfo}
            setAnimalInfo={setAnimalInfo}
            validate={validate}
            setValid={setAnimalFormIsValid}
          />
        </div>
      </div>
      {attachments.length > 1 && (
        <div className="on-farm-add-animal-photos-row">
          {attachments.map(attachment => (
            <button
              key={attachment.attachmentId}
              type="button"
              className={`photo-thumbnail${selectedAttachment?.attachmentId === attachment.attachmentId ? ' selected' : ''}`}
              onClick={() => setSelectedAttachmentId(attachment.attachmentId)}>
              <img src={attachment.accessToken} alt="Animal Photo" />
            </button>
          ))}
        </div>
      )}
      <div className="button-row">
        <button className="button medium green inverted" type="button" onClick={() => onCancel()} disabled={creatingAnimal}>
          {BUTTON_CONSTANTS.CANCEL}
        </button>
        <button
          className={!animalFormIsValid || creatingAnimal || isOnFarmLocked ? 'button medium green disabled' : 'button medium green'}
          type="button"
          onClick={createNewAnimal}
          disabled={creatingAnimal || isOnFarmLocked}>
          {BUTTON_CONSTANTS.SUBMIT}
        </button>
      </div>
    </div>
  );
};

export default OnFarmAddAnimal;
